import { defineStore } from "pinia";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { links } from "%/dynamic-component-editor/enums/link-enums";
import { $axios, titleToKebabCase } from "@bloglovin/vue-framework";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { uniqueId } from "lodash";
import { useStagingFrameStore } from "%/dynamic-component-editor/stores/staging-frame-store";
import { useStyleStore } from "@vcl";
import Publication from "%/dynamic-component-editor/classes/publication";

export const useDynamicComponentStore = defineStore("dynamic-component-store", {
  state: () => ({
    dynamicComponents: {},
    dynamicComponentVariants: {},
    parentDynamicComponents: {},
    dynamicAppComponents: {},
    dynamicComponentName: null,
    dynamicComponentId: null,
    dynamicComponentClasses: {},
    isCreatingComponentVariationForId: null,
    isSettingVariantActive: false,
    isDeletingComponentVariationForId: null,
    componentScreenshots: {},
    context: "",
    entityId: "",
  }),
  getters: {
    apiV1() {
      return useGeneralStore().getLink(links.api);
    },
    standardDynamicComponents() {
      return useConfigurationStore().dynamicComponents;
    },
    customDynamicComponents() {
      return useConfigurationStore().customDynamicComponents;
    },
    standardDynamicPages() {
      return useConfigurationStore().dynamicPages;
    },
    customDynamicPages() {
      return useConfigurationStore().customDynamicPages;
    },
    allDynamicComponents() {
      return {
        ...this.dynamicComponents,
        ...this.parentDynamicComponents,
      };
    },
    dynamicPages() {
      return {
        ...this.standardDynamicPages,
        ...this.customDynamicPages,
      };
    },
    allDynamicComponentsFromHierarchy() {
      return {
        ...this.dynamicComponents,
        ...this.parentDynamicComponents,
        ...this.dynamicAppComponents,
      };
    },
    themeId() {
      let id = Object.keys(useStyleStore().themeIdAppIdMap).findLast(themeId => {
        return useStyleStore().themeIdAppIdMap[themeId] === useGeneralStore().appId;
      });
      if (!id || Number.isNaN(id)) {
        id = 0;
      }

      return id;
    },
    themeMode() {
      return useStyleStore().getThemeMode(this.themeId);
    },
  },
  actions: {
    addDynamicComponents(componentConfigs) {
      const componentKeys = Object.keys(componentConfigs);
      componentKeys.forEach(key => {
        this.dynamicComponents[key] = componentConfigs[key];
      });
    },
    updateDynamicComponentVersion(dynamicComponentId) {
      $axios.post(this.apiV1 + `/dynamic-component/${dynamicComponentId}/sync`, {
        publication_id: this.entityId,
      }).then(response => {
        if (this.dynamicComponents[dynamicComponentId]) {
          this.dynamicComponents[dynamicComponentId] = response.data.config;
        } else if (this.parentDynamicComponents[dynamicComponentId]) {
          this.parentDynamicComponents[dynamicComponentId] = response.data.config;
        }
        this.dynamicComponentVariants = {
          ...this.dynamicComponentVariants,
          ...response.data.updatedVariant,
        };
        Publication.get(this.entityId).version = response.data.publicationStagingVersionId;
      });
    },
    cloneDynamicComponent(dynamicComponentId, dynamicComponentVersionId, callback = () => {
    }) {
      $axios.post(this.apiV1 + `/dynamic-component/${dynamicComponentId}/${dynamicComponentVersionId}/clone`, {
        publication_id: this.entityId,
      }).then(response => {
        this.dynamicComponents[response.data.config.dynamic_component_id] = response.data.config;
        this.dynamicComponentVariants = {
          ...this.dynamicComponentVariants,
          ...response.data.updatedVariant,
        };
        Publication.get(this.entityId).version = response.data.publicationStagingVersionId;
        this.dynamicComponentId = response.data.config.dynamic_component_id;
        callback(response.data.config);
      });
    },
    selectAndApplyComponentVariant(componentId, componentVersionId, successCallback) {
      $axios.post(this.apiV1 + "/dynamic-component/select-and-apply", {
        dynamic_component_id: componentId,
        dynamic_component_version_id: componentVersionId,
        publication_id: this.entityId,
      }).then(response => {
        const dynamicComponentId = response.data.dynamicComponentId;
        const dynamicComponentConfig = response.data.dynamicComponentConfig;
        Publication.get(this.entityId).version = response.data.publicationStagingVersionId;
        this.addComponentConfigAndSetActive(
          dynamicComponentId,
          dynamicComponentConfig,
        );
        successCallback(response.data);
      }).catch(error => {
        throw new DynamicComponentError("Failed to select and apply dynamic component", {
          error,
        });
      });
    },
    addComponentConfigAndSetActive(dynamicComponentId, dynamicComponentVersionId) {
      const config = this.getDynamicComponentConfigFromHierarchy(dynamicComponentId);
      this.dynamicComponentVariants = {
        ...this.dynamicComponentVariants,
        [config.class]: {
          "dynamic_component_id": dynamicComponentId,
          "dynamic_component_version_id": dynamicComponentVersionId,
        },
      };
    },
    getDynamicComponentClassByComponentName(componentName) {
      const dynamicComponentConfig = Object.values(this.dynamicComponents).find(config => {
        return config.name === componentName;
      });

      if (!dynamicComponentConfig) {
        throw new DynamicComponentError("Dynamic component not found for component with specified name", {
          componentName,
        });
      }
      return dynamicComponentConfig.class;
    },
    setDynamicComponentVariantActive(
      dynamicComponentId,
      dynamicComponentClass,
      dynamicComponentVersionId,
      publicationId,
      success = () => {
      },
    ) {
      const previousVariant = useStagingFrameStore().standardDynamicComponentVariants[dynamicComponentClass];
      this.isSettingVariantActive = true;
      useStagingFrameStore().standardDynamicComponentVariants[dynamicComponentClass] = {
        "dynamic_component_id": dynamicComponentId,
        "dynamic_component_version_id": dynamicComponentVersionId,
      };
      $axios.post(this.apiV1 + `/dynamic-component/${dynamicComponentId}/${dynamicComponentVersionId}/active`, {
        publication_id: publicationId,
      }).then(response => {
        Publication.get(this.entityId).version = response.data.publicationStagingVersionId;
        success({
          publicationStagingVersionId: response.data.publicationStagingVersionId,
        });
      }).catch(() => {
        useStagingFrameStore().standardDynamicComponentVariants[dynamicComponentClass] = previousVariant;
      }).finally(() => {
        this.isSettingVariantActive = false;
      });
    },
    deleteDynamicComponent(dynamicComponentId) {
      this.isDeletingComponentVariationForId = dynamicComponentId;
      $axios.post(this.apiV1 + `/dynamic-component/${dynamicComponentId}/delete`, {}).then(response => {
        delete this.dynamicComponents[dynamicComponentId];

        Publication.get(this.entityId).version = response.data.publicationStagingVersionId;
        useStagingFrameStore().removeComponentFromPageConfig(dynamicComponentId);
        useStagingFrameStore().removeComponentFromComponentConfig(dynamicComponentId);
      }).catch(() => {
        // TODO: handle error
      }).finally(() => {
        this.isDeletingComponentVariationForId = null;
      });
    },
    duplicateDynamicComponent(dynamicComponentId, dynamicComponentVersionId, callback = () => {
    }) {
      this.isCreatingComponentVariationForId = dynamicComponentId;
      $axios.post(this.apiV1 + `/dynamic-component/${dynamicComponentId}/${dynamicComponentVersionId}/duplicate`, {
        publication_id: this.entityId,
      }).then(response => {
        const componentClassName = this.dynamicComponents[dynamicComponentId].class;
        const dynamicComponent = response.data.dynamicComponent;

        this.dynamicComponents[dynamicComponent["dynamic_component_id"]] = {
          ...dynamicComponent,
          class: componentClassName,
        };

        Publication.get(this.entityId).version = response.data.publicationStagingVersionId;
        useStagingFrameStore().addComponentToStandardComponentOrPageConfig(
          dynamicComponent["dynamic_component_id"],
          componentClassName,
          dynamicComponent.name,
          dynamicComponent.rootOptions["display-name"],
          dynamicComponent.rootOptions["page-slug"],
        );

        callback(response.data.dynamicComponent);
      }).catch(() => {
        // TODO: handle error
      }).finally(() => {
        this.isCreatingComponentVariationForId = null;
      });
    },
    getComponentByClassName(className) {
      let id = this.dynamicComponentVariants[className]?.dynamic_component_id || this.dynamicComponentVariants[className];
      if (!id) {
        return Object.values(this.allDynamicComponents).find(dynamicComponentConfig => dynamicComponentConfig.class === className);
      }
      return this.allDynamicComponents[id];
    },
    getDynamicComponentConfigFromHierarchy(componentId) {
      return this.allDynamicComponentsFromHierarchy[componentId];
    },
    getDynamicComponentConfig(dynamicComponentName) {
      return Object.values(this.dynamicComponents).find(dynamicComponentConfig => dynamicComponentConfig.name === dynamicComponentName);
    },
    getDynamicComponentConfigById(dynamicComponentId) {
      return Object.values(this.dynamicComponents).find(dynamicComponentConfig => {
        return dynamicComponentConfig.dynamic_component_id === dynamicComponentId || dynamicComponentConfig.dynamicComponentId === dynamicComponentId;
      });
    },
    getDynamicComponentConfigurationFromParentById(componentId) {
      return useDynamicComponentStore().parentDynamicComponents[componentId];
    },
    async getOrImportDynamicComponentClass(dynamicComponentClassName) {
      if (this.dynamicComponentClasses[dynamicComponentClassName]) {
        return this.dynamicComponentClasses[dynamicComponentClassName];
      }
      let kebabCaseDynamicComponentClassName = titleToKebabCase(dynamicComponentClassName);
      // TODO: DB needs to be updated
      if (kebabCaseDynamicComponentClassName === "publication-page") {
        kebabCaseDynamicComponentClassName = "custom-page";
      }

      await Promise.any([
        import(`~/dynamic-components/standard-pages/${kebabCaseDynamicComponentClassName}/${kebabCaseDynamicComponentClassName}.js`),
        import(`~/dynamic-components/standard-components/${kebabCaseDynamicComponentClassName}/${kebabCaseDynamicComponentClassName}.js`),
        import(`%/dynamic-component-editor/classes/${kebabCaseDynamicComponentClassName}.js`),
      ]).then(module => {
        this.dynamicComponentClasses[dynamicComponentClassName] = module.default;
      }).catch(() => {
        throw new DynamicComponentError("Dynamic component class not found", {
          dynamicComponentClassName,
          kebabCaseDynamicComponentClassName,
        });
      });
      return this.dynamicComponentClasses[dynamicComponentClassName];
    },
    getDynamicComponentConfigurationByName(dynamicComponentName) {
      return Object.values(this.dynamicComponents).find(config => config.name === dynamicComponentName);
    },
    getDynamicComponentConfigurationById(dynamicComponentId) {
      return this.dynamicComponents[dynamicComponentId];
    },
    getDynamicComponentConfigurationLayout(dynamicComponentName) {
      const dynamicComponentConfig = Object.values(this.allDynamicComponentsFromHierarchy).find(config => config.name === dynamicComponentName);
      return dynamicComponentConfig.layout;
    },
    getDynamicComponentConfigurationLayoutId(dynamicComponentId) {
      const config = this.getDynamicComponentConfigFromHierarchy(dynamicComponentId);
      if (!config) {
        throw new DynamicComponentError(
          "Dynamic component config not found",
          { dynamicComponentId },
        );
      }
      return config.layout;
    },
    async fetchComponentConfigurationLayout(componentConfiguration) {
      const dynamicComponentId = componentConfiguration.dynamic_component_id;
      const dynamicComponentVersionId = componentConfiguration.dynamic_component_version_id;
      const response = await $axios.get(
        // eslint-disable-next-line @stylistic/js/comma-dangle
        `${this.apiV1}/dynamic-component/${dynamicComponentId}/${dynamicComponentVersionId}/${this.context}/${this.entityId}/layout`
      );
      const dynamicComponentConfig = response.data.data;
      const layout = response.data.data.layout;
      this.dynamicComponents[dynamicComponentId] = response.data.data;
      Publication.get(this.entityId).version = response.data.publicationStagingVersionId;

      return {
        dynamicComponentConfig,
        layout,
      };
    },
    async getOrFetchDynamicComponentByIdAndVersion(dynamicComponentId, dynamicComponentVersionId) {
      return (await $axios.get(`${this.apiV1}/dynamic-component/${dynamicComponentId}/${dynamicComponentVersionId}`)).data;
    },
    async getOrFetchDynamicComponentById(dynamicComponentId) {
      return (await $axios.get(`${this.apiV1}/dynamic-component/${dynamicComponentId}`)).data;
    },
    async getOrFetchDynamicComponentConfigurationLayoutById(dynamicComponentId) {
      let dynamicComponentConfig = Object.values(this.allDynamicComponentsFromHierarchy).find(config => {
        return config.dynamic_component_id === dynamicComponentId || config.dynamicComponentId === dynamicComponentId;
      });

      let layout;
      if (!dynamicComponentConfig) {
        dynamicComponentConfig = (await $axios.get(`${this.apiV1}/dynamic-component/${dynamicComponentId}`)).data;
        if (dynamicComponentConfig.rootOptions["dynamic-app-id"]) {
          this.dynamicAppComponents[dynamicComponentId] = dynamicComponentConfig;
        } else {
          this.dynamicComponents[dynamicComponentId] = dynamicComponentConfig;
        }
      }

      if (dynamicComponentConfig.layout === undefined) {
        const componentConfiguration = useConfigurationStore().getDynamicComponentConfigurationById(dynamicComponentId);
        // this is to prevent an error from showing on old publications with the incorrect configuration format which does not
        // have the required data needed to fetch the layout from the API with this endpoint.
        if (componentConfiguration) {
          const {
            dynamicComponentConfig: config,
            layout: configLayout,
          } = await this.fetchComponentConfigurationLayout(componentConfiguration);
          layout = configLayout;
          dynamicComponentConfig = config;
        } else {
          throw new DynamicComponentError(
            "Trying to create a dynamic component for an ID that should already exist",
            {
              dynamicComponentId,
            },
          );
        }
      } else {
        layout = dynamicComponentConfig.compiledConfiguration ?? dynamicComponentConfig.layout;
      }
      await this.getOrImportDynamicComponentClass(dynamicComponentConfig.class);
      return this.initialize(layout, dynamicComponentConfig.rootOptions);
    },
    getDynamicComponentConfigByName(dynamicComponentName) {
      const componentConfig = Object.values(this.allDynamicComponentsFromHierarchy).find(config => {
        // there was a discrepancy introduced where component names sometimes have spaces and sometimes not,
        // here normalize the name matches
        const componentNameWithoutSpaces = config.name.replaceAll(" ", "");
        const componentNameToMatchWithoutSpaces = dynamicComponentName.replaceAll(" ", "");
        return componentNameWithoutSpaces === componentNameToMatchWithoutSpaces;
      });

      if (!componentConfig) {
        return undefined;
      }
      const variant = this.dynamicComponentVariants[componentConfig.class];
      if (variant) {
        return this.allDynamicComponentsFromHierarchy[variant.dynamic_component_id];
      }
      return this.allDynamicComponentsFromHierarchy[componentConfig.dynamic_component_id];
    },
    async getOrFetchDynamicComponentConfigurationLayout(dynamicComponentName, isPage) {
      let dynamicComponentConfig = this.getDynamicComponentConfigByName(dynamicComponentName);
      let layout;
      if (dynamicComponentConfig === undefined) {
        const componentConfiguration = useConfigurationStore().getDynamicComponentConfigurationByName(dynamicComponentName);
        // this is to prevent an error from showing on old publications with the incorrect configuration format which does not
        // have the required data needed to fetch the layout from the API with this endpoint.
        if (componentConfiguration) {
          const {
            dynamicComponentConfig: config,
            layout: configLayout,
          } = await this.fetchComponentConfigurationLayout(componentConfiguration);
          layout = configLayout;
          dynamicComponentConfig = config;
        } else {
          const response = await $axios.post(`${this.apiV1}/dynamic-component/${this.entityId}/create`, {
            dynamic_component_name: dynamicComponentName,
            is_page: isPage,
          });
          const dynamicComponentId = response.data.dynamic_component_id;
          useConfigurationStore().addDynamicComponent({
            "component-name": dynamicComponentName,
            dynamic_component_class: response.data.class,
            dynamic_component_id: dynamicComponentId,
            dynamic_component_name: dynamicComponentName,
            dynamic_component_type: response.data.component_type,
            dynamic_component_version_id: response.data.dynamic_component_version_id,
          });
          layout = response.data.layout;
          response.data.name = dynamicComponentName;
          dynamicComponentConfig = response.data;
          this.dynamicComponents[dynamicComponentId] = response.data;
          useStagingFrameStore().updatePageIdInStandardPageConfig(dynamicComponentName, dynamicComponentId);
        }
      } else {
        const applicableComponentVariant = this.dynamicComponentVariants[dynamicComponentConfig.class];
        if (applicableComponentVariant) {
          return this.getOrFetchDynamicComponentConfigurationLayoutById(
            applicableComponentVariant.dynamic_component_id,
          );
        }
        layout = dynamicComponentConfig.compiledConfiguration ?? dynamicComponentConfig.layout;
      }
      return this.initialize(layout, dynamicComponentConfig.rootOptions);
    },
    initialize(layout, rootOptions = {}, forceIdentifier = false) {
      if (forceIdentifier || !layout.identifier) {
        layout.identifier = uniqueId(`dynamic-element-${Date.now()}`);
      }
      if (!layout.timestampLastUpdated) {
        layout.timestampLastUpdated = Date.now();
      }

      layout.rootOptions = rootOptions;
      if (layout.contents?.length) {
        for (let i = 0; i < layout.contents.length; i++) {
          this.initialize(layout.contents[i], rootOptions, forceIdentifier);
        }
      }

      return layout;
    },
  },
});

export default useDynamicComponentStore;
